import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  MousePointerClick, Activity, ShieldAlert, UserCheck, Clock, Database, Sparkles,
  ChevronLeft, ChevronRight,
} from "lucide-react";

const STEPS = [
  {
    icon: MousePointerClick,
    title: "1 · Choose a process",
    body: "Pick one of three governed bioprocesses. Each loads pre-seeded telemetry so you start from a realistic operating point.",
  },
  {
    icon: Activity,
    title: "2 · Watch live governance",
    body: "Four panels refresh every 2 seconds: the telemetry feed (pH, dissolved O₂, temperature, nutrient), the Lyapunov stability meter V(x), the metabolic budget Bₜ, and the FSM process stage. A healthy system keeps V(x) below 1.00.",
  },
  {
    icon: ShieldAlert,
    title: "3 · Trigger a fault",
    body: "Press “Trigger Lyapunov Fault” to inject an unsafe command. V(x) spikes above 1.00, the governor intercepts it, flashes a red quarantine, and freezes telemetry to the safe holding profile — the bad command never reaches the process.",
  },
  {
    icon: UserCheck,
    title: "4 · Human-in-the-loop approval",
    body: "In the HITL panel you review the intercepted fault, enter a correction note, and approve. Only a human can clear the quarantine — the governor never self-clears. On approval the correction is sealed to the ledger.",
  },
  {
    icon: Clock,
    title: "5 · Live recovery ramp",
    body: "Approval does not snap back to normal. The system enters a RECOVERING state and V(x) ticks down every second while a live countdown runs. The header stays amber and controls stay locked until V(x) drops below 1.00 — only then does it turn green and resume nominal governance.",
  },
  {
    icon: Database,
    title: "6 · Sealed audit trail",
    body: "Each approval writes a Canonical entry with dual Ed25519 signatures and a BLAKE2b hash-chained Merkle root. Hover a root to see the prior root it links to, click Verify to re-check, or Simulate Tamper to watch the chain break.",
  },
  {
    icon: Sparkles,
    title: "7 · Analyze & Library",
    body: "Once the system is back to green nominal, click Analyze for a data-grounded breakdown of the event, then Save to Library. The Library tab keeps every saved analysis — read-only and persistent across refreshes.",
  },
];

export const GuideDialog = ({ open, onOpenChange }) => {
  const [i, setI] = useState(0);
  const step = STEPS[i];
  const Icon = step.icon;
  const last = i === STEPS.length - 1;

  const close = () => {
    onOpenChange(false);
    setI(0);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="bg-[#0b1120] border-slate-800 text-slate-200 max-w-lg">
        <DialogHeader>
          <DialogTitle className="mono text-xs uppercase tracking-widest text-cyan-400">
            How BioGovernor works
          </DialogTitle>
          <DialogDescription className="sr-only">
            A step-by-step walkthrough of the BioGovernor governance and audit workflow.
          </DialogDescription>
        </DialogHeader>
        <div className="py-1" data-testid="guide-dialog">
          <div className="h-12 w-12 rounded-xl bg-cyan-950/50 border border-cyan-800/50 flex items-center justify-center mb-4">
            <Icon className="h-6 w-6 text-cyan-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-100 mb-2">{step.title}</h3>
          <p className="text-sm text-slate-400 leading-relaxed min-h-[96px]">{step.body}</p>
          <div className="flex items-center gap-1.5 mt-4">
            {STEPS.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${
                  idx === i ? "w-6 bg-cyan-400" : "w-1.5 bg-slate-700"
                }`}
              />
            ))}
            <span className="ml-auto mono text-[10px] text-slate-500">
              {i + 1} / {STEPS.length}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between mt-1">
          <Button
            variant="ghost"
            disabled={i === 0}
            onClick={() => setI(i - 1)}
            className="mono text-xs text-slate-400 hover:text-slate-200 disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {last ? (
            <Button
              data-testid="guide-done-button"
              onClick={close}
              className="mono text-xs font-bold bg-cyan-500 hover:bg-cyan-400 text-[#060A12]"
            >
              Got it
            </Button>
          ) : (
            <Button
              data-testid="guide-next-button"
              onClick={() => setI(i + 1)}
              className="mono text-xs font-bold bg-cyan-500 hover:bg-cyan-400 text-[#060A12]"
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
